/**
 * Formatters e normalizadores para dados do fluxo BAAS
 */

export const toSafeString = (value) => {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string') return value;
  return String(value);
};

/**
 * Remove todos os caracteres não numéricos de uma string
 * @param {string} value - Valor a ser processado
 * @returns {string} String contendo apenas dígitos
 */
export const onlyDigits = (value = '') => toSafeString(value).replace(/\D/g, '');

/**
 * Remove todos os caracteres que nao sejam letras A-Z ou digitos e normaliza para maiusculo.
 * @param {string} value - Valor a ser processado
 * @returns {string} String contendo apenas letras sem acento e digitos
 */
export const onlyAlphaNumericUpper = (value = '') =>
  toSafeString(value)
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, '');

/**
 * Normaliza data de DD/MM/YYYY ou YYYY-MM-DD para DD-MM-YYYY (formato esperado pela API do banking)
 * @param {string} value - Data no formato DD/MM/YYYY ou YYYY-MM-DD
 * @returns {string} Data no formato DD-MM-YYYY ou valor original se inválido
 */
export const normalizeDate = (value = '') => {
  if (!value) return '';
  const safeValue = toSafeString(value);

  if (/^\d{4}-\d{2}-\d{2}$/.test(safeValue)) {
    const [year, month, day] = safeValue.split('-');
    return `${day}-${month}-${year}`;
  }

  const digits = onlyDigits(safeValue);
  if (digits.length !== 8) return safeValue;
  const day = digits.slice(0, 2);
  const month = digits.slice(2, 4);
  const year = digits.slice(4);
  return `${day}-${month}-${year}`;
};

/**
 * Formata telefone para (XX) XXXXX-XXXX ou (XX) XXXX-XXXX
 * @param {string} phone - Telefone sem formatação
 * @returns {string} Telefone formatado
 */
export const formatPhone = (phone = '') => {
  if (!phone) return '';
  const safePhone = toSafeString(phone);
  const digits = safePhone.replace(/\D/g, '');
  if (digits.length === 10) {
    return `(${digits.slice(0, 2)}) ${digits.slice(2, 6)}-${digits.slice(6)}`;
  }
  if (digits.length === 11) {
    return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
  }
  return safePhone;
};

/**
 * Formata telefone com código do país (+55) para envio na API
 * Remove formatação e adiciona prefixo +55
 * @param {string} phone - Telefone com ou sem formatação
 * @returns {string} Telefone no formato +55XXXXXXXXXXX
 */
export const formatPhoneWithCountryCode = (phone = '') => {
  if (!phone) return '';
  const safePhone = toSafeString(phone);
  const digits = onlyDigits(safePhone);
  if (digits.length === 10 || digits.length === 11) {
    return `+55${digits}`;
  }
  if (safePhone.startsWith('+55')) {
    return `+55${onlyDigits(safePhone.replace('+55', ''))}`;
  }
  return safePhone;
};

/**
 * Formata CEP para XXXXX-XXX
 * @param {string} cep - CEP sem formatação
 * @returns {string} CEP formatado
 */
export const formatCep = (cep = '') => {
  if (!cep) return '';
  const safeCep = toSafeString(cep);
  const digits = safeCep.replace(/\D/g, '');
  if (digits.length === 8) {
    return `${digits.slice(0, 5)}-${digits.slice(5)}`;
  }
  return safeCep;
};

/**
 * Formata o valor para apenas números.
 * @param {string} value - Valor a ser formatado
 * @returns {string} - Exemplo: 123456789
 */
export function justNumberFormat(value) {
  if (!value) return '';

  return toSafeString(value).replace(/\D/g, '');
}

/**
 * Formata data de YYYY-MM-DD para DD/MM/YYYY ou mantém DD/MM/YYYY
 * @param {string} date - Data em qualquer formato
 * @returns {string} Data no formato DD/MM/YYYY
 */
export const formatDate = (date = '') => {
  if (!date) return '';
  const safeDate = toSafeString(date);
  if (/^\d{4}-\d{2}-\d{2}$/.test(safeDate)) {
    const [year, month, day] = safeDate.split('-');
    return `${day}/${month}/${year}`;
  }
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(safeDate)) {
    return safeDate;
  }
  return safeDate;
};

/**
 * Formata CPF para XXX.XXX.XXX-XX
 * @param {string} cpf - CPF sem formatação
 * @returns {string} CPF formatado
 */
export const formatCpf = (cpf = '') => {
  if (!cpf) return '';
  const safeCpf = toSafeString(cpf);
  const digits = safeCpf.replace(/\D/g, '');
  if (digits.length === 11) {
    return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6, 9)}-${digits.slice(9)}`;
  }
  return safeCpf;
};

/**
 * Formata CNPJ para XX.XXX.XXX/XXXX-XX
 * @param {string} cnpj - CNPJ sem formatação
 * @returns {string} CNPJ formatado
 */
export const formatCnpj = (cnpj = '') => {
  if (!cnpj) return '';
  const safeCnpj = toSafeString(cnpj);
  const normalized = onlyAlphaNumericUpper(safeCnpj);
  if (normalized.length === 14) {
    return `${normalized.slice(0, 2)}.${normalized.slice(2, 5)}.${normalized.slice(5, 8)}/${normalized.slice(
      8,
      12
    )}-${normalized.slice(12)}`;
  }
  return safeCnpj;
};
